import { useEffect, useRef } from 'react';
import { useSwiper, useSwiperSlide } from 'swiper/react';

import useThrottling from '@/hooks/useThrottling';

const OVERFLOW_VALUES = new Set(['auto', 'scroll', 'overlay']);
const EDGE_TOLERANCE = 2;
const WHEEL_DELTA_THRESHOLD = 120;
const WHEEL_RESET_TIMEOUT = 280;
const SWIPE_THRESHOLD = 50;         // hoisted so all tuning lives together

function isScrollableElement(element) {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  const styles = window.getComputedStyle(element);
  const overflowY = styles?.overflowY || styles?.overflow;
  const overflowX = styles?.overflowX || styles?.overflow;

  // Vertical scroll detection (current use case)
  const canScrollY =
    OVERFLOW_VALUES.has(overflowY) &&
    (element.scrollHeight - element.clientHeight > EDGE_TOLERANCE);

  // Horizontal scroll detection (future-proof; harmless today)
  const canScrollX =
    OVERFLOW_VALUES.has(overflowX) &&
    (element.scrollWidth - element.clientWidth > EDGE_TOLERANCE);

  return canScrollY || canScrollX;
}

function findScrollableParent(startNode, root) {
  if (!root) {
    return null;
  }

  let current = startNode instanceof HTMLElement ? startNode : startNode?.parentElement || null;

  while (current && current !== root) {
    if (isScrollableElement(current)) {
      return current;
    }

    current = current.parentElement;
  }

  return root;
}

function getMaxScrollTop(node) {
  if (!node) {
    return 0;
  }

  return Math.max(node.scrollHeight - node.clientHeight, 0);
}

function isAtTop(node) {
  if (!node) {
    return true;
  }

  return node.scrollTop <= EDGE_TOLERANCE;
}

function isAtBottom(node) {
  if (!node) {
    return true;
  }

  const maxScrollTop = getMaxScrollTop(node);
  return node.scrollTop >= maxScrollTop - EDGE_TOLERANCE;
}

function getTouchScreenY(touch) {
  if (!touch) {
    return null;
  }

  if (typeof touch.screenY === 'number') {
    return touch.screenY;
  }

  if (typeof touch.clientY === 'number') {
    return touch.clientY;
  }

  return null;
}

export default function useArtworkSlider(scrollableDiv) {
  const throttle = useThrottling();
  const touchStartScreenPosition = useRef(null);
  const touchStartSlideIndex = useRef(null);
  const touchScrollableElement = useRef(null);
  const touchStartScrollTop = useRef(0);
  const ignoreTouch = useRef(false);
  const wheelAccumulator = useRef({
    accumulated: 0,
    direction: 0,
    element: null,
    resetTimer: null,
  });
  const wheelEdgeIntent = useRef({
    armed: false,
    direction: 0,
    element: null,
  });
  const swiper = useSwiper();
  const swiperSlide = useSwiperSlide();

  useEffect(() => () => {
    if (wheelAccumulator.current.resetTimer) {
      clearTimeout(wheelAccumulator.current.resetTimer);
    }
  }, []);

  function normalizeWheelDelta(event) {
    const { deltaY, deltaMode } = event;

    if (deltaMode === 1) {
      return deltaY * 16;
    }

    if (deltaMode === 2) {
      if (typeof window !== 'undefined' && typeof window.innerHeight === 'number') {
        return deltaY * window.innerHeight;
      }

      return deltaY * 100;
    }

    return deltaY;
  }

  function resetWheelAccumulator() {
    if (wheelAccumulator.current.resetTimer) {
      clearTimeout(wheelAccumulator.current.resetTimer);
      wheelAccumulator.current.resetTimer = null;
    }

    wheelAccumulator.current.accumulated = 0;
    wheelAccumulator.current.direction = 0;
    wheelAccumulator.current.element = null;
    wheelEdgeIntent.current.armed = false;
    wheelEdgeIntent.current.direction = 0;
    wheelEdgeIntent.current.element = null;
  }

  function scheduleWheelAccumulatorReset() {
    if (wheelAccumulator.current.resetTimer) {
      clearTimeout(wheelAccumulator.current.resetTimer);
    }

    wheelAccumulator.current.resetTimer = setTimeout(() => {
      wheelAccumulator.current.accumulated = 0;
      wheelAccumulator.current.direction = 0;
      wheelAccumulator.current.element = null;
      wheelAccumulator.current.resetTimer = null;
      wheelEdgeIntent.current.armed = false;
      wheelEdgeIntent.current.direction = 0;
      wheelEdgeIntent.current.element = null;
    }, WHEEL_RESET_TIMEOUT);
  }

  function isSliderLockedTarget(target) {
    if (!(target instanceof Element)) {
      return false;
    }

    const lockingNode = target.closest('[data-artwork-slider-lock], .artwork-slider-lock');
    if (!lockingNode) {
      return false;
    }

    if (!scrollableDiv?.current) {
      return true;
    }

    return scrollableDiv.current.contains(lockingNode);
  }

  function onTouchStart(event) {
    if (!scrollableDiv.current) {
      return;
    }

    ignoreTouch.current = false;
    touchStartSlideIndex.current = swiper.activeIndex;

    if (isSliderLockedTarget(event.target)) {
      ignoreTouch.current = true;
      touchStartSlideIndex.current = null;
      touchScrollableElement.current = null;
      touchStartScrollTop.current = 0;
      touchStartScreenPosition.current = null;
      return;
    }

    const primaryTouch = event.touches[0];
    const screenY = getTouchScreenY(primaryTouch);

    if (screenY === null) {
      touchStartSlideIndex.current = null;
      touchStartScreenPosition.current = null;
      return;
    }

    const scrollableElement = findScrollableParent(event.target, scrollableDiv.current);
    touchScrollableElement.current = scrollableElement;
    touchStartScrollTop.current = scrollableElement?.scrollTop || 0;
    // Use screen coordinates so viewport resizes (e.g. keyboard) do not fake swipes
    touchStartScreenPosition.current = screenY;
  }

  function onTouchEnd(event) {
    const startedSlideIndex = touchStartSlideIndex.current;
    touchStartSlideIndex.current = null;

    if (ignoreTouch.current) {
      ignoreTouch.current = false;
      touchScrollableElement.current = null;
      touchStartScreenPosition.current = null;
      touchStartScrollTop.current = 0;
      return;
    }

    if (!swiperSlide.isActive || touchStartScreenPosition.current === null
      || startedSlideIndex === null || swiper.activeIndex !== startedSlideIndex || swiper.animating) {
      touchScrollableElement.current = null;
      touchStartScreenPosition.current = null;
      touchStartScrollTop.current = 0;
      return;
    }

    const scrollableElement = touchScrollableElement.current || scrollableDiv.current;
    const newScreenPosition = getTouchScreenY(event.changedTouches[0]);

    if (newScreenPosition === null) {
      touchScrollableElement.current = null;
      touchStartScreenPosition.current = null;
      touchStartScrollTop.current = 0;
      return;
    }

    const swipeDelta = newScreenPosition - touchStartScreenPosition.current;

    touchScrollableElement.current = null;
    touchStartScreenPosition.current = null;

    if (Math.abs(swipeDelta) < SWIPE_THRESHOLD) {
      touchStartScrollTop.current = 0;
      return;
    }

    const startScrollTop = touchStartScrollTop.current;
    const maxScrollTop = getMaxScrollTop(scrollableElement);
    const startedAtTop = startScrollTop <= EDGE_TOLERANCE;
    const startedAtBottom = startScrollTop >= maxScrollTop - EDGE_TOLERANCE;
    const endedAtTop = isAtTop(scrollableElement);
    const endedAtBottom = isAtBottom(scrollableElement);
    const hadScrollableContent = maxScrollTop > EDGE_TOLERANCE;

    if (swipeDelta > 0) {
      if (!hadScrollableContent || (startedAtTop && endedAtTop)) {
        swiper.slidePrev();
      }
    } else if (swipeDelta < 0) {
      if (!hadScrollableContent || (startedAtBottom && endedAtBottom)) {
        swiper.slideNext();
      }
    }

    touchStartScrollTop.current = 0;
  }

  function onWheel(event) {
    if (!scrollableDiv.current || !swiperSlide.isActive) {
      return;
    }

    if (isSliderLockedTarget(event.target)) {
      resetWheelAccumulator();
      return;
    }

    const scrollableElement = findScrollableParent(event.target, scrollableDiv.current);
    const normalizedDeltaY = normalizeWheelDelta(event);

    if (normalizedDeltaY === 0) {
      return;
    }

    const direction = normalizedDeltaY > 0 ? 1 : -1;
    const targetElement = scrollableElement || scrollableDiv.current;
    const maxScrollTop = getMaxScrollTop(scrollableElement);
    const canScrollUp = maxScrollTop > EDGE_TOLERANCE && !isAtTop(scrollableElement);
    const canScrollDown = maxScrollTop > EDGE_TOLERANCE && !isAtBottom(scrollableElement);

    if ((direction < 0 && canScrollUp) || (direction > 0 && canScrollDown)) {
      resetWheelAccumulator();
      return;
    }

    const edgeIntent = wheelEdgeIntent.current;
    const shouldArmEdgeIntent = !edgeIntent.armed
      || edgeIntent.direction !== direction
      || edgeIntent.element !== targetElement;

    if (shouldArmEdgeIntent) {
      edgeIntent.armed = true;
      edgeIntent.direction = direction;
      edgeIntent.element = targetElement;
      wheelAccumulator.current.accumulated = 0;
      wheelAccumulator.current.direction = direction;
      wheelAccumulator.current.element = targetElement;
      scheduleWheelAccumulatorReset();
      return;
    }

    if (wheelAccumulator.current.direction !== direction || wheelAccumulator.current.element !== targetElement) {
      wheelAccumulator.current.accumulated = 0;
    }

    wheelAccumulator.current.direction = direction;
    wheelAccumulator.current.element = targetElement;
    wheelAccumulator.current.accumulated += Math.abs(normalizedDeltaY);
    scheduleWheelAccumulatorReset();

    if (wheelAccumulator.current.accumulated < WHEEL_DELTA_THRESHOLD) {
      return;
    }

    resetWheelAccumulator();

    throttle(() => {
      if (direction < 0) {
        swiper.slidePrev();
      } else {
        swiper.slideNext();
      }
    });
  }

  return { onTouchStart, onTouchEnd, onWheel };
}
